// Unsecured Loan verification — one page for legacy /AccountsApproval/VerifyUnsecuredLoan (New),
// VerifyUnsecuredLoanExisting (top-up) and VerifyUnsecuredLoanReturn (repayment), which share
// VerifyNewLoanView and one approve action (loanType picks the SP server-side).
// Legacy keeps "Return" for New / Existing loans and drops it for repayments. Loans returned for
// update (Status '0') are corrected on the Unsecured Loan entry screen, as in legacy.
import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { toast } from 'react-toastify';
import { Wallet, Clock, Building2, RotateCcw } from 'lucide-react';

import InboxHeader from '../../components/Inbox/InboxHeader';
import ActionButtons from '../../components/Inbox/ActionButtons';
import RemarksHistory from '../../components/Inbox/RemarksHistory';
import InboxSplitLayout from '../../components/Inbox/InboxSplitLayout';
import { SimpleCommentInput } from '../../components/Inbox/VerificationInput';
import { formatIndianCurrency } from '../../utilities/amountToTextHelper';

import {
    fetchUnsecuredLoanQueue,
    fetchUnsecuredLoanDetail,
    submitApproveLoan,
    clearUnsecuredLoanDetail,
    resetVerifyUnsecuredLoan,
    selectVULQueue, selectVULQueueLoading, selectVULQueueError,
    selectVULDetail, selectVULDetailLoading, selectVULDetailError,
    selectVULVerifyLoading,
} from '../../slices/accountsSlice/verifyUnsecuredLoanSlice';

import {
    fetchStatusList,
    selectEnabledActions,
    selectHasActions,
    selectStatusListLoading,
    selectStatusListError,
    resetApprovalData,
    setShowReturnButton,
} from '../../slices/CommonSlice/getStatusSlice';

import {
    fetchRemarks,
    selectRemarks,
    selectRemarksLoading,
    setSelectedMOID,
} from '../../slices/supplierPOSlice/purcahseHelperSlice';

const TYPE_TEXT = {
    New: { title: 'Unsecured Loan (New) Verification', noun: 'new loan', dateLabel: 'Loan Date' },
    Existing: { title: 'Unsecured Loan (Existing) Verification', noun: 'loan top-up', dateLabel: 'Additional Loan Date' },
    Return: { title: 'Unsecured Loan (Return) Verification', noun: 'loan repayment', dateLabel: 'Return Date' },
};

const ACTION_VERB = {
    Verify: 'Verified Successfully',
    Approve: 'Approved Successfully',
    Return: 'Returned for Update',
    Reject: 'Rejected Successfully',
};

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

const Field = ({ label, value, className = '' }) => (
    <div className={className}>
        <p className="text-[10px] font-bold text-gray-400 uppercase tracking-wider mb-0.5">{label}</p>
        <p className="text-sm font-semibold text-gray-800 dark:text-gray-100 break-words">{value || value === 0 ? value : '—'}</p>
    </div>
);

const StatusBadge = ({ status }) => (String(status) === '0' ? (
    <span className="px-2 py-0.5 text-xs rounded-full border bg-amber-100 text-amber-800 border-amber-200 dark:bg-amber-900/30 dark:text-amber-300 whitespace-nowrap">Return</span>
) : (
    <span className="px-2 py-0.5 text-xs rounded-full border bg-indigo-100 text-indigo-800 border-indigo-200 dark:bg-indigo-900/30 dark:text-indigo-300 whitespace-nowrap">Verification Pending</span>
));

const formatAmount = (v) => (v != null && v !== '' ? `₹ ${formatIndianCurrency(v)}` : null);
// Queue dates arrive as ISO DateTimes; show them as dd-MMM-yyyy like the detail endpoint does.
const displayDate = (v) => {
    if (!v) return '';
    if (/^\d{1,2}-[A-Za-z]{3}-\d{4}$/.test(v)) return v;
    const d = new Date(v);
    return Number.isNaN(d.getTime()) ? v : `${String(d.getDate()).padStart(2, '0')}-${MONTHS[d.getMonth()]}-${d.getFullYear()}`;
};

const VerifyUnsecuredLoan = ({ loanType = 'New', notificationData, onNavigate }) => {
    const text = TYPE_TEXT[loanType] || TYPE_TEXT.New;
    const isRepayment = loanType === 'Return';

    const dispatch = useDispatch();
    const { userData, userDetails } = useSelector((s) => s.auth);
    const roleId = userData?.roleId || userData?.RID;

    const queue = useSelector(selectVULQueue);
    const queueLoading = useSelector(selectVULQueueLoading);
    const queueError = useSelector(selectVULQueueError);
    const detail = useSelector(selectVULDetail);
    const detailLoading = useSelector(selectVULDetailLoading);
    const detailError = useSelector(selectVULDetailError);
    const verifyLoading = useSelector(selectVULVerifyLoading);

    const statusLoading = useSelector(selectStatusListLoading);
    const statusError = useSelector(selectStatusListError);
    const enabledActions = useSelector(selectEnabledActions);
    const hasActions = useSelector(selectHasActions);

    const remarks = useSelector(selectRemarks);
    const remarksLoading = useSelector(selectRemarksLoading);

    const { InboxTitle, ModuleDisplayName } = notificationData || {};

    const [selectedItem, setSelectedItem] = useState(null);
    const [comment, setComment] = useState('');
    const [showRemarksHistory, setShowRemarksHistory] = useState(false);
    const [searchQuery, setSearchQuery] = useState('');
    const [isLeftPanelCollapsed, setIsLeftPanelCollapsed] = useState(false);
    const [isLeftPanelHovered, setIsLeftPanelHovered] = useState(false);

    const getCurrentUser = () => userData?.userName || userDetails?.userName || 'system';
    const showReturn = isRepayment ? 'No' : 'Yes';

    const isReturned = String(selectedItem?.Status) === '0';
    // Only trust the detail that belongs to the row currently selected.
    const loan = selectedItem && detail?.BankTransactionRefNo === selectedItem.BankTransactionRefNo ? detail : null;

    useEffect(() => {
        dispatch(resetVerifyUnsecuredLoan());
        setSelectedItem(null);
        if (roleId) dispatch(fetchUnsecuredLoanQueue({ roleId, type: loanType }));
    }, [roleId, loanType, dispatch]);

    useEffect(() => {
        dispatch(setShowReturnButton(showReturn));
        return () => {
            dispatch(resetVerifyUnsecuredLoan());
            dispatch(resetApprovalData());
        };
    }, [dispatch, showReturn]);

    useEffect(() => {
        if (!selectedItem) return;
        dispatch(clearUnsecuredLoanDetail());
        dispatch(resetApprovalData());
        dispatch(setShowReturnButton(showReturn));
        if (String(selectedItem.Status) !== '0') {
            dispatch(fetchUnsecuredLoanDetail({ transRefNo: selectedItem.BankTransactionRefNo, type: loanType }));
        }
        setComment('');
        setShowRemarksHistory(false);
        setIsLeftPanelCollapsed(true);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedItem]);

    // The queue row carries no MOID; the detail does. Remarks are keyed on the bank transaction ref no.
    useEffect(() => {
        if (!loan?.MOID || !roleId) return;
        dispatch(fetchStatusList({ MOID: loan.MOID, ROID: roleId, ChkAmt: 0 }));
        dispatch(setSelectedMOID(loan.MOID));
        dispatch(fetchRemarks({ trno: loan.BankTransactionRefNo, moid: loan.MOID }));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [loan?.MOID, loan?.BankTransactionRefNo]);

    const handleBackToInbox = () => {
        if (onNavigate) onNavigate('dashboard', { name: 'Dashboard', type: 'dashboard' });
    };

    const handleRefresh = () => {
        if (roleId) dispatch(fetchUnsecuredLoanQueue({ roleId, type: loanType }));
    };

    const finishAndRefresh = () => {
        setTimeout(() => {
            dispatch(fetchUnsecuredLoanQueue({ roleId, type: loanType }));
            setSelectedItem(null);
            setComment('');
            setShowRemarksHistory(false);
            setIsLeftPanelCollapsed(false);
            dispatch(clearUnsecuredLoanDetail());
            dispatch(resetApprovalData());
        }, 800);
    };

    const handleActionClick = async (action) => {
        if (!loan) { toast.error(`No ${text.noun} selected`); return; }
        if (!comment.trim()) { toast.error('Enter Note'); return; }

        const actionValue = action.type;
        // ApproveLoan passes no deduction parameters (legacy's repayment branch read a table that
        // is never rendered, so it always sent none either).
        const payload = {
            BankTransactionRefNo: loan.BankTransactionRefNo,
            Action: actionValue,
            Roleid: roleId,
            ApprovalNote: comment.trim(),
            Createdby: getCurrentUser(),
            Type: loan.LoanType || loanType,
            Bank: loan.Bank,
            paymentAmount: loan.paymentAmount,
            LoanNo: loan.LoanNo,
        };

        try {
            const result = await dispatch(submitApproveLoan(payload)).unwrap();
            const status = typeof result === 'string' ? result : result?.Data;
            // The spApproveUnsLoan* success literal is the single-t "Submited".
            if (status === 'Submited' || status === 'Submitted') {
                toast.success(`Unsecured Loan ${ACTION_VERB[actionValue] || `${actionValue}d`}`);
                finishAndRefresh();
                return;
            }
            toast.error(status || 'Error Occurred');
        } catch (error) {
            toast.error(typeof error === 'string' ? error : 'Error Occurred');
        }
    };

    const filteredQueue = queue.filter((item) => {
        if (!searchQuery) return true;
        const q = searchQuery.toLowerCase();
        return item.LoanNo?.toLowerCase().includes(q)
            || item.Name?.toLowerCase().includes(q)
            || String(item.LoanAmount ?? '').includes(q);
    });

    const renderItemCard = (item) => (
        <div className="p-4">
            <div className="flex items-center space-x-3 mb-2">
                <div className="w-12 h-12 rounded-full border-2 border-indigo-200 dark:border-indigo-600 bg-gradient-to-br from-indigo-100 to-indigo-100 dark:from-indigo-800/50 dark:to-indigo-800/50 flex items-center justify-center">
                    <Wallet className="w-5 h-5 text-indigo-600 dark:text-indigo-400" />
                </div>
                <div className="flex-1 min-w-0">
                    <h3 className="font-semibold text-gray-900 dark:text-white text-sm truncate">{item.Name}</h3>
                    <p className="text-xs text-gray-500 dark:text-gray-400 truncate">Loan {item.LoanNo}</p>
                </div>
                <span className="text-sm font-bold text-gray-900 dark:text-white whitespace-nowrap">{formatAmount(item.LoanAmount)}</span>
            </div>
            <div className="flex items-center justify-between gap-2 text-xs">
                <span className="text-gray-500 dark:text-gray-400">{displayDate(item.PaymentDate)}</span>
                <StatusBadge status={item.Status} />
            </div>
        </div>
    );

    const renderListItem = (item) => (
        <div className="flex items-center gap-x-6 gap-y-1 flex-wrap text-sm">
            <span className="font-mono bg-gray-100 dark:bg-gray-700 px-1.5 py-0.5 rounded text-xs">{item.LoanNo}</span>
            <span className="font-semibold text-gray-900 dark:text-white min-w-[180px] truncate">{item.Name}</span>
            <span className="text-gray-500 dark:text-gray-400 min-w-[100px]">{displayDate(item.PaymentDate)}</span>
            <span className="font-semibold text-gray-900 dark:text-white">{formatAmount(item.LoanAmount)}</span>
            <span className="ml-auto"><StatusBadge status={item.Status} /></span>
        </div>
    );

    const renderCollapsedItem = () => (
        <div className="w-full h-full rounded-lg border-2 border-indigo-200 dark:border-indigo-600 bg-gradient-to-br from-indigo-100 to-indigo-100 dark:from-indigo-800/50 dark:to-indigo-800/50 flex items-center justify-center">
            <Wallet className="w-4 h-4 text-indigo-600 dark:text-indigo-400" />
        </div>
    );

    const renderDetails = () => (
        <div className="bg-white dark:bg-gray-800 rounded-2xl p-5 border-2 border-gray-200 dark:border-gray-700 space-y-5">
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                <Field label="Loan No" value={loan.LoanNo} />
                <Field label={text.dateLabel} value={loan.ReturnLoanDate} />
                <Field label="Name" value={loan.Name} />
                {isRepayment ? (
                    <>
                        <Field label="Principal Repaid" value={formatAmount(loan.RePayPrinciple)} />
                        <Field label="Interest Paid" value={formatAmount(loan.RePayInterest)} />
                        <Field label="TDS Amount" value={formatAmount(loan.TDSAmount)} />
                    </>
                ) : (
                    <>
                        <Field label="Loan Amount" value={formatAmount(loan.LoanAmount)} />
                        <Field label="Rate of Interest (%)" value={loan.RateOfIntrest} />
                    </>
                )}
            </div>

            {loanType === 'New' && (loan.NatureGroupName || loan.GroupName) && (
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 pt-4 border-t border-gray-200 dark:border-gray-700">
                    <Field label="Nature of Group" value={loan.NatureGroupName} />
                    <Field label="Group Name" value={loan.GroupName} />
                    <Field label="Sub Group Name" value={loan.SubGroupName} />
                    <Field label="Opening Balance" value={formatAmount(loan.OpeningBalance)} />
                    <Field label="Balance As On Date" value={loan.BalanceAsOnDate} />
                    <Field label="Ledger Value Type" value={loan.LedgerValueType} />
                </div>
            )}

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 pt-4 border-t border-gray-200 dark:border-gray-700">
                <Field label="Bank Name" value={loan.Bank} />
                <Field label="Transaction Number" value={loan.UnsLoanPaymentNo} />
                <Field label="Transaction Date" value={loan.ReturnPayDate} />
                <Field label="Transaction Amount" value={formatAmount(loan.paymentAmount)} />
                <Field label="Mode of Pay" value={loan.ModeofPay} />
                <Field label="Remarks" value={loan.Remarks} className="sm:col-span-2 lg:col-span-3" />
            </div>
        </div>
    );

    const renderActions = () => {
        if (statusLoading) {
            return (
                <div className="bg-white dark:bg-gray-800 rounded-xl p-6 border border-gray-200 dark:border-gray-700">
                    <div className="flex items-center justify-center space-x-3">
                        <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-indigo-600"></div>
                        <span className="text-gray-600 dark:text-gray-400">Loading actions…</span>
                    </div>
                </div>
            );
        }
        if (statusError) {
            return (
                <div className="bg-red-50 dark:bg-red-900/20 rounded-xl p-6 border border-red-200 dark:border-red-700">
                    <p className="text-red-600 dark:text-red-400 text-center">⚠️ Error loading actions: {statusError}</p>
                </div>
            );
        }
        if (!hasActions || !enabledActions || enabledActions.length === 0) {
            return (
                <div className="bg-yellow-50 dark:bg-yellow-900/20 rounded-xl p-6 border border-yellow-200 dark:border-yellow-700">
                    <p className="text-yellow-700 dark:text-yellow-400 text-center">ℹ️ No actions available for this {text.noun}</p>
                </div>
            );
        }
        return (
            <ActionButtons
                actions={enabledActions}
                onActionClick={handleActionClick}
                loading={verifyLoading}
                isVerified={true}
                comment={comment}
                showValidation={true}
            />
        );
    };

    const renderDetailContent = () => {
        if (!selectedItem) return null;

        if (isReturned) {
            return (
                <div className="bg-amber-50 dark:bg-amber-900/20 rounded-2xl p-6 border-2 border-amber-200 dark:border-amber-700 flex items-start gap-3">
                    <RotateCcw className="w-5 h-5 text-amber-700 dark:text-amber-400 flex-shrink-0 mt-0.5" />
                    <div>
                        <h3 className="text-sm font-bold text-amber-800 dark:text-amber-300">Loan {selectedItem.LoanNo} was returned for update</h3>
                        <p className="text-sm text-amber-700 dark:text-amber-400 mt-1">Correct and resubmit it from the Unsecured Loan entry screen — there is nothing to verify until it is resubmitted.</p>
                    </div>
                </div>
            );
        }
        if (detailLoading || (!loan && !detailError)) {
            return (
                <div className="flex items-center justify-center gap-3 py-12">
                    <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-indigo-600"></div>
                    <span className="text-sm text-gray-500 dark:text-gray-400">Loading loan details…</span>
                </div>
            );
        }
        if (detailError || !loan) {
            return (
                <div className="bg-red-50 dark:bg-red-900/20 rounded-xl p-6 border border-red-200 dark:border-red-700">
                    <p className="text-red-600 dark:text-red-400 text-center">⚠️ {detailError || 'Loan details could not be loaded — it may no longer be pending at your level.'}</p>
                </div>
            );
        }

        return (
            <div className="space-y-6">
                <div className="bg-gradient-to-r from-indigo-50 to-blue-50 dark:from-indigo-900/20 dark:to-blue-900/20 rounded-2xl p-6 border-2 border-indigo-200 dark:border-indigo-700">
                    <h2 className="text-xl font-bold text-gray-900 dark:text-white mb-1">{loan.Name}</h2>
                    <p className="text-indigo-600 dark:text-indigo-400 font-semibold mb-3">Loan {loan.LoanNo} · {formatAmount(loan.paymentAmount)}</p>
                    <div className="flex flex-wrap gap-2">
                        <span className="px-3 py-1 bg-indigo-100 dark:bg-indigo-900/30 text-indigo-700 dark:text-indigo-300 rounded-full text-xs font-medium">{loan.LoanType || loanType}</span>
                        <span className="px-3 py-1 bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded-full text-xs font-medium flex items-center gap-1">
                            <Building2 className="w-3 h-3" /> {loan.Bank}
                        </span>
                        <StatusBadge status={selectedItem.Status} />
                    </div>
                </div>

                {renderDetails()}

                <RemarksHistory
                    isOpen={showRemarksHistory}
                    onToggle={() => setShowRemarksHistory(!showRemarksHistory)}
                    remarks={remarks}
                    loading={remarksLoading}
                    title="Approval History"
                />

                <SimpleCommentInput
                    comment={comment}
                    onCommentChange={(e) => setComment(e.target.value)}
                    label="Notes"
                    placeholder="Enter verification notes…"
                    required
                    rows={3}
                    showCharCount
                />
                {renderActions()}
            </div>
        );
    };

    return (
        <div className="space-y-6">
            <InboxHeader
                title={`${InboxTitle || text.title} (${queue.length})`}
                subtitle={ModuleDisplayName}
                itemCount={queue.length}
                onBackClick={handleBackToInbox}
                HeaderIcon={Wallet}
                badgeText={text.title}
                badgeCount={queue.length}
                searchConfig={{
                    enabled: true,
                    placeholder: 'Search by loan no, name, amount…',
                    value: searchQuery,
                    onChange: (e) => setSearchQuery(e.target.value),
                }}
                enableViewToggle
            />

            <InboxSplitLayout
                isLeftPanelCollapsed={isLeftPanelCollapsed}
                onLeftPanelCollapseToggle={setIsLeftPanelCollapsed}
                isLeftPanelHovered={isLeftPanelHovered}
                onLeftPanelHoverChange={setIsLeftPanelHovered}
                left={{
                    items: filteredQueue,
                    selectedItem,
                    onItemSelect: setSelectedItem,
                    renderItem: renderItemCard,
                    renderListItem,
                    renderCollapsedItem,
                    loading: queueLoading,
                    error: queueError,
                    onRefresh: handleRefresh,
                    config: {
                        title: 'Pending',
                        icon: Clock,
                        emptyMessage: `No ${text.noun}s pending verification!`,
                        itemKey: 'BankTransactionRefNo',
                        enableCollapse: true,
                        enableRefresh: true,
                        enableHover: true,
                        maxHeight: '100%',
                        headerGradient: 'from-indigo-50 to-blue-50 dark:from-indigo-900/20 dark:to-blue-900/20',
                    },
                    renderPopupContent: () => renderDetailContent(),
                    popupConfig: {
                        title: text.title,
                        icon: Wallet,
                        headerGradient: 'from-indigo-50 to-blue-50 dark:from-indigo-900/20 dark:to-blue-900/20',
                        maxWidth: 'max-w-[80vw]',
                    },
                }}
                right={{
                    selectedItem,
                    loading: false,
                    renderContent: renderDetailContent,
                    config: {
                        title: 'Loan Details',
                        icon: Wallet,
                        selectedTitle: text.title,
                        emptyTitle: 'No Loan Selected',
                        emptyMessage: `Select a ${text.noun} from the list to view details and take action.`,
                        headerGradient: 'from-indigo-50 to-blue-50 dark:from-indigo-900/20 dark:to-blue-900/20',
                        maxHeight: 'calc(100vh-200px)',
                        sticky: true,
                        stickyTop: '1.5rem',
                    },
                }}
            />
        </div>
    );
};

export default VerifyUnsecuredLoan;
